// The form button (FR-016, FR-047; research R14): 44 px, `button` radius, `pr` fill with white semi-bold
// 15 px, or the outline variant. While `pending` it shows progress and cannot be pressed again.
import { ActivityIndicator, StyleSheet } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";

import { PressableTarget, type PressableTargetProps } from "./PressableTarget";
import { Text } from "./Text";

export type ButtonProps = Omit<
  PressableTargetProps,
  "children" | "style" | "accessibilityLabel" | "onPress"
> & {
  children: string;
  /** Optional when a parent (expo-router `Link asChild`) supplies it. */
  onPress?: PressableTargetProps["onPress"];
  variant?: "primary" | "outline";
  pending?: boolean;
  disabled?: boolean;
  testID?: string;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    base: {
      minHeight: t.size.button,
      borderRadius: t.radius.button,
      paddingHorizontal: t.space.lg,
      flexDirection: "row",
      gap: t.space.sm,
      alignSelf: "stretch",
    },
    primary: { backgroundColor: t.color.primary },
    // Metro secondary: a 2 px frame in the text colour, no fill.
    outline: {
      backgroundColor: "transparent",
      borderWidth: t.borderWidth.input,
      borderColor: t.color.text,
    },
    disabled: { opacity: 0.6 },
    primaryText: { color: t.onPrimary },
    // Metro action labels: lowercase (the accessible name keeps the written case).
    label: { textTransform: "lowercase" },
  }),
);

export function Button({
  children,
  onPress,
  variant = "primary",
  pending = false,
  disabled = false,
  testID,
  ...rest
}: ButtonProps) {
  const styles = useStyles();
  const theme = useTheme();
  const inactive = pending || disabled;
  return (
    <PressableTarget
      {...rest}
      accessibilityLabel={children}
      accessibilityState={{ disabled: inactive, busy: pending }}
      aria-busy={pending}
      disabled={inactive}
      onPress={onPress}
      style={[styles.base, styles[variant], inactive && styles.disabled]}
      testID={testID}
    >
      {pending ? (
        <ActivityIndicator
          size="small"
          color={variant === "primary" ? theme.onPrimary : theme.color.text}
        />
      ) : null}
      <Text
        variant="heading"
        weight="semiBold"
        size="md"
        style={[styles.label, variant === "primary" && styles.primaryText]}
      >
        {children}
      </Text>
    </PressableTarget>
  );
}
